import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Download, Mail, Search, Send, Trash2, X, Check } from "lucide-react";

const STATUS_OPTIONS = [
  { v: "new", label: "New", color: "text-[var(--bloom-ink)]" },
  { v: "invited", label: "Invited", color: "text-[var(--bloom-warning)]" },
  { v: "subscribed", label: "Subscribed", color: "text-[var(--bloom-accent)]" },
  { v: "declined", label: "Declined", color: "text-[var(--bloom-ink-muted)]" },
];

export default function ConsoleSubscribers() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/waitlist");
    setEntries(data.entries || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = entries.filter((e) => {
    const matchQ = q ? [e.name, e.email, e.region, e.notes].some((v) => (v || "").toLowerCase().includes(q.toLowerCase())) : true;
    const s = e.status || "new";
    const matchStatus = statusFilter ? s === statusFilter : true;
    return matchQ && matchStatus;
  });

  const counts = entries.reduce((acc, e) => {
    const s = e.status || "new";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const changeStatus = async (id, status) => {
    const { data } = await api.post(`/admin/waitlist/${id}/status`, { status });
    setEntries((arr) => arr.map((e) => e.id === id ? data.entry : e));
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this person from the waitlist?")) return;
    await api.delete(`/admin/waitlist/${id}`);
    setEntries((arr) => arr.filter((e) => e.id !== id));
  };

  const exportCsv = () => {
    const url = `${api.defaults.baseURL}/admin/waitlist/export.csv`;
    window.open(url, "_blank");
  };

  return (
    <div data-testid="console-subscribers">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-1">Heritage Bloom Club</div>
          <h1 className="font-serif text-5xl md:text-6xl leading-none">Subscribers</h1>
          <p className="font-serif italic text-lg text-[var(--bloom-ink-soft)] mt-3">The people waiting for their first Bloom Box.</p>
        </div>
        <button onClick={exportCsv} className="bloom-btn bloom-btn-outline" data-testid="export-waitlist-btn"><Download size={14} /> Export CSV</button>
      </div>

      {/* Status tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <button onClick={() => setStatusFilter("")} className={`text-left p-3 border transition ${!statusFilter ? "border-[var(--bloom-ink)] bg-[var(--bloom-surface)]" : "border-[var(--bloom-border)] bg-white/40 hover:bg-white/70"}`} data-testid="subscribers-tile-all">
          <div className="bloom-label" style={{ fontSize: "0.65rem" }}>All</div>
          <div className="font-serif text-2xl">{entries.length}</div>
        </button>
        {STATUS_OPTIONS.map((s) => (
          <button key={s.v} onClick={() => setStatusFilter(statusFilter === s.v ? "" : s.v)} className={`text-left p-3 border transition ${statusFilter === s.v ? "border-[var(--bloom-ink)] bg-[var(--bloom-surface)]" : "border-[var(--bloom-border)] bg-white/40 hover:bg-white/70"}`} data-testid={`subscribers-tile-${s.v}`}>
            <div className={`bloom-label ${s.color}`} style={{ fontSize: "0.65rem" }}>{s.label}</div>
            <div className="font-serif text-2xl">{counts[s.v] || 0}</div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 border border-[var(--bloom-border)] px-3 py-2 bg-white/50 mb-6 max-w-md" data-testid="subscribers-search">
        <Search size={14} className="text-[var(--bloom-ink-muted)]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, region…" className="flex-1 bg-transparent outline-none text-sm" />
        {q && <button onClick={() => setQ("")} className="text-[var(--bloom-ink-muted)]"><X size={14} /></button>}
      </div>

      {loading ? (
        <div className="text-center py-12 font-serif italic text-[var(--bloom-ink-muted)]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bloom-card p-12 text-center font-serif italic text-[var(--bloom-ink-muted)]" data-testid="subscribers-empty">No one matches.</div>
      ) : (
        <div className="border border-[var(--bloom-border)] overflow-x-auto bg-white/40" data-testid="subscribers-table">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bloom-surface)] text-left">
              <tr>
                <Th>Joined</Th><Th>Name</Th><Th>Email</Th><Th>Region</Th><Th>Box</Th><Th>Cadence</Th><Th>Status</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const status = e.status || "new";
                return (
                  <tr key={e.id} className="border-t border-[var(--bloom-border)] hover:bg-white/40" data-testid={`subscribers-row-${e.id}`}>
                    <Td><span className="text-xs">{new Date(e.created_at).toLocaleDateString()}</span></Td>
                    <Td><div className="font-serif">{e.name}</div>{e.notes && <div className="text-[11px] italic text-[var(--bloom-ink-muted)] mt-0.5">{e.notes}</div>}</Td>
                    <Td><a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 underline text-xs"><Mail size={11} /> {e.email}</a></Td>
                    <Td>{e.region || <em className="text-[var(--bloom-ink-muted)]"></em>}</Td>
                    <Td className="capitalize">{e.box_size || "·"}</Td>
                    <Td className="capitalize">{e.frequency || "·"}</Td>
                    <Td>
                      <select value={status} onChange={(ev) => changeStatus(e.id, ev.target.value)} className="text-xs border border-[var(--bloom-border)] bg-white/60 px-2 py-1 tracking-[0.05em] uppercase" data-testid={`subscribers-status-${e.id}`}>
                        {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                      </select>
                    </Td>
                    <Td>
                      <div className="flex gap-1">
                        {status !== "invited" && (
                          <button onClick={() => changeStatus(e.id, "invited")} title="Mark invited" className="p-1.5 hover:bg-[var(--bloom-surface)] text-[var(--bloom-ink)]" data-testid={`subscribers-invite-${e.id}`}><Send size={13} /></button>
                        )}
                        <button onClick={() => remove(e.id)} title="Remove" className="p-1.5 hover:bg-[var(--bloom-surface)] text-[var(--bloom-accent)]" data-testid={`subscribers-delete-${e.id}`}><Trash2 size={13} /></button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] font-medium">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>; }
